/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Users, X, UserPlus, Search, SearchX } from 'lucide-react';
import { PeopleSidebarItem, SidebarPersonDetail } from './people/PeopleSidebarItem';
import { PeopleSearchFlyout } from './people/PeopleSearchFlyout';

export type { SidebarPersonDetail };

export interface PeopleSidebarProps {
  people: string[];
  peopleData?: Map<string, SidebarPersonDetail>;
  activeDragId: string | number | null;
  droppedZoneId: string | null;
  filterAssignee: string;
  activeVorgangNames?: Set<string>;
  onToggleFilter: (name: string) => void;
  onOpenContactCard: (name: string) => void;
  onClearFilter?: () => void;
  onAddPerson?: (personData?: { name?: string; role?: string; organization?: string; email?: string; phone?: string }) => void;
}

const PeopleSidebar: React.FC<PeopleSidebarProps> = ({ 
  people, 
  peopleData,
  activeDragId, 
  droppedZoneId,
  filterAssignee,
  activeVorgangNames,
  onToggleFilter,
  onOpenContactCard,
  onClearFilter,
  onAddPerson
}) => {
  const isActiveDrag = activeDragId !== null;
  const isFilterActive = Boolean(filterAssignee && filterAssignee !== 'all');

  const [searchInput, setSearchInput] = useState('');
  const deferredSearch = React.useDeferredValue(searchInput);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchBtnRef = useRef<HTMLButtonElement>(null);
  const [searchCoords, setSearchCoords] = useState<{ top: number; right: number } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFlyoutRef = useRef<HTMLDivElement>(null);

  const stopWords = useMemo(() => new Set([
    'dr', 'prof', 'mag', 'di', 'ing', 'bsc', 'msc', 'dipl', 'fh', 'und', 'and', 'or', 'von', 'van', 'der', 'zu', 'gmbh', 'co', 'kg'
  ]), []);

  const filteredPeople = useMemo(() => {
    const q = deferredSearch.toLowerCase().trim();
    if (!q) return people;

    return people.filter(p => {
      const pLower = p.toLowerCase();
      if (pLower.includes(q)) return true;

      const rawParts = p.split(/[\s.,/\\()_+\-&]+/).filter(Boolean);
      const cleanParts = rawParts.filter(part => !stopWords.has(part.toLowerCase()));
      const parts = cleanParts.length > 0 ? cleanParts : rawParts;
      const initials = parts.map(part => part[0]).join('').toLowerCase();
      if (initials.includes(q)) return true;

      const detail = peopleData?.get(p);
      if (detail) {
        if (detail.role && detail.role.toLowerCase().includes(q)) return true;
        if (detail.organization && detail.organization.toLowerCase().includes(q)) return true;
        if (detail.email && detail.email.toLowerCase().includes(q)) return true;
        if (detail.phone && detail.phone.toLowerCase().includes(q)) return true;
      }

      return false;
    });
  }, [people, deferredSearch, stopWords, peopleData]);

  const updateSearchCoords = useCallback(() => {
    if (searchBtnRef.current) {
      const rect = searchBtnRef.current.getBoundingClientRect();
      setSearchCoords({
        top: Math.max(16, rect.top - 6),
        right: window.innerWidth - rect.left + 12
      });
    }
  }, []);

  const handleToggleSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateSearchCoords();
    setIsSearchOpen(prev => {
      const next = !prev;
      if (next) {
        setTimeout(() => searchInputRef.current?.focus(), 60);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!isSearchOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        searchFlyoutRef.current && 
        !searchFlyoutRef.current.contains(target) &&
        searchBtnRef.current &&
        !searchBtnRef.current.contains(target)
      ) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const handleResize = () => updateSearchCoords();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSearchOpen, updateSearchCoords]);

  const handleClear = useCallback(() => {
    if (onClearFilter) {
      onClearFilter();
    } else {
      onToggleFilter('all');
    }
  }, [onClearFilter, onToggleFilter]);

  return (
    <div data-tour="people-sidebar" className="flex flex-col h-full bg-slate-955 text-white items-center select-none w-20">
      {/* Pinned Top Controls Header */}
      <div className="w-full shrink-0 flex flex-col items-center gap-2.5 px-2 pt-3 pb-2 border-b border-white/5 bg-slate-955 z-10">
        {/* "Alle" (All) button */}
        <div className="p-0.5">
          <div
            title={isFilterActive ? `Filter aufheben (aktiver Filter: ${filterAssignee})` : "Alle Personen ausgewählt"}
            onClick={handleClear}
            className={`
              group relative flex flex-col items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 border-2 cursor-pointer select-none
              ${!isFilterActive
                ? 'bg-indigo-600/20 border-indigo-500/60 text-indigo-300 shadow-md shadow-indigo-500/10'
                : 'bg-white/5 hover:bg-rose-500/10 border-white/10 hover:border-rose-500/30 text-slate-400 hover:text-rose-300'
              }
            `}
          >
            {isFilterActive ? (
              <>
                <X size={14} className="stroke-[2.5] text-rose-400 group-hover:scale-110 group-hover:rotate-90 transition-transform" />
                <span className="text-[7.5px] font-bold uppercase tracking-tight text-rose-300">
                  Alle
                </span>
              </>
            ) : (
              <>
                <Users size={14} className="stroke-[2.5] text-indigo-400" />
                <span className="text-[7.5px] font-black uppercase tracking-tight text-indigo-300">
                  Alle
                </span>
              </>
            )}
          </div>
        </div>

        {/* "+ Person" Button */}
        <div className="p-0.5">
          <button
            type="button"
            title="Neue Person / Kontakt zum Projekt hinzufügen"
            onClick={() => onAddPerson?.()}
            className="group relative flex flex-col items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 border-2 border-dashed border-white/20 hover:border-indigo-400 hover:bg-indigo-500/15 text-slate-400 hover:text-indigo-300 cursor-pointer select-none active:scale-95 shadow-sm"
          >
            <UserPlus size={16} className="stroke-[2.2] group-hover:scale-110 transition-transform" />
            <span className="text-[7.5px] font-black uppercase tracking-tight text-slate-400 group-hover:text-indigo-300 mt-0.5">
              + Person
            </span>
          </button>
        </div>

        {/* Search Toggle Button */}
        <div className="p-0.5 relative">
          <button
            ref={searchBtnRef}
            type="button"
            title={isSearchOpen ? "Suche schließen" : "Personen durchsuchen (Filter)"}
            onClick={handleToggleSearch}
            className={`
              group relative flex flex-col items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 border-2 cursor-pointer select-none active:scale-95 shadow-sm
              ${searchInput.trim()
                ? 'bg-indigo-600/30 border-indigo-400 text-indigo-300 ring-2 ring-indigo-500/20'
                : isSearchOpen
                  ? 'bg-white/15 border-indigo-400 text-indigo-300'
                  : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20 text-slate-400 hover:text-white'
              }
            `}
          >
            {searchInput.trim() ? (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 text-white rounded-full text-[9px] font-black flex items-center justify-center shadow-md">
                {filteredPeople.length}
              </span>
            ) : null}
            <Search size={15} className={`transition-transform ${isSearchOpen ? 'scale-110 text-indigo-400' : 'group-hover:scale-110'}`} />
            <span className="text-[7.5px] font-black uppercase tracking-tight mt-0.5">
              Suche
            </span>
          </button>
        </div>
      </div>

      {/* Scrollable list of people */}
      <div className="flex-1 w-full overflow-y-auto custom-scrollbar flex flex-col items-center gap-2.5 px-2 py-3">
        {filteredPeople.map(person => (
          <PeopleSidebarItem 
            key={person} 
            name={person} 
            detail={peopleData?.get(person)}
            isActiveDrag={isActiveDrag}
            isDropped={droppedZoneId === `assignee-${person}`}
            isFilterActive={filterAssignee === person}
            hasActiveVorgang={activeVorgangNames ? activeVorgangNames.has(person.toLowerCase()) : false}
            onToggleFilter={onToggleFilter}
            onOpenContactCard={onOpenContactCard}
          />
        ))}
        {filteredPeople.length === 0 && people.length > 0 && (
          <div className="text-center py-4 px-1 flex flex-col items-center justify-center text-slate-500 gap-1.5">
            <SearchX size={16} className="text-slate-600" />
            <span className="text-[9px] font-bold">Kein Treffer</span>
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="text-[8px] text-indigo-400 hover:text-indigo-300 underline font-semibold mt-0.5"
            >
              Suche leeren
            </button>
          </div>
        )}
        {people.length === 0 && (
          <div className="text-center py-6 px-1 flex flex-col items-center justify-center text-slate-600 gap-1">
            <span className="text-[10px] font-bold">Keine</span>
            <span className="text-[8px]">Personen</span>
          </div>
        )}
      </div>

      <PeopleSearchFlyout
        isSearchOpen={isSearchOpen}
        searchCoords={searchCoords}
        searchFlyoutRef={searchFlyoutRef}
        searchInputRef={searchInputRef}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        setIsSearchOpen={setIsSearchOpen}
        filteredPeople={filteredPeople}
        totalPeopleCount={people.length}
        peopleData={peopleData}
        filterAssignee={filterAssignee}
        activeVorgangNames={activeVorgangNames}
        stopWords={stopWords}
        isFilterActive={isFilterActive}
        onToggleFilter={onToggleFilter}
        onClear={handleClear}
      />
    </div>
  );
};

export default React.memo(PeopleSidebar);
